import BusinessIcon from '@mui/icons-material/Business';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import UpdateIcon from '@mui/icons-material/Update';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import { apiService } from '../services/api';
import { Client } from '../types';
import ClientEditDialog from './ClientEditDialog';
import ErrorMessage from './ErrorMessage';
import LoadingSpinner from './LoadingSpinner';

const ClientList: React.FC = () => {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [newClient, setNewClient] = useState({ name: '', country: '', shortDescription: '' });
  const [adding, setAdding] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [movingClientId, setMovingClientId] = useState<string | null>(null);
  const { user } = useUser();
  const navigate = useNavigate();

  const fetchClients = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiService.getClients();
      setClients(res.data || res);
    } catch (err: any) {
      setError(err.message || 'Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, []);

  const handleAddClient = async () => {
    if (!newClient.name.trim() || !newClient.country.trim()) return;
    setAdding(true);
    setError(null);
    try {
      const response = await apiService.createClient(newClient);
      // The request method extracts .data, so response is { data: Client }
      const createdClient = response.data;
      
      // Add the new client to the local state instead of refetching
      setClients(prev => [...prev, createdClient]);
      setShowAdd(false);
      setNewClient({ name: '', country: '', shortDescription: '' });
    } catch (err: any) {
      setError(err.message || 'Failed to add client');
    } finally {
      setAdding(false);
    }
  };

  const handleDeleteClient = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this client?')) return;
    setDeletingId(id);
    setError(null);
    try {
      await apiService.deleteClient(id);
      // Remove the client from local state instead of refetching
      setClients(prev => prev.filter(client => client.id !== id));
    } catch (err: any) {
      setError(err.message || 'Failed to delete client');
    } finally {
      setDeletingId(null);
    }
  };

  const handleEditClient = (client: Client) => {
    setEditingClient(client);
    setShowEditDialog(true);
  };

  const handleClientUpdated = (updatedClient: Client) => {
    setClients(prev => prev.map(client =>
      client.id === updatedClient.id ? updatedClient : client
    ));
    setEditingClient(null);
    setShowEditDialog(false);
  };

  const handleCloseEditDialog = () => {
    setEditingClient(null);
    setShowEditDialog(false);
  };

  const handleMoveClientUp = async (clientId: string) => {
    setMovingClientId(clientId);
    setError(null);
    
    // Optimistic update - immediately swap the clients in the UI
    const currentIndex = clients.findIndex(c => c.id === clientId);
    if (currentIndex > 0) {
      const optimisticClients = [...clients];
      [optimisticClients[currentIndex - 1], optimisticClients[currentIndex]] = 
      [optimisticClients[currentIndex], optimisticClients[currentIndex - 1]];
      setClients(optimisticClients);
    }
    
    try {
      const updatedClient = await apiService.moveClientUp(clientId);
      
      // Update with the actual server response
      setClients(prev => 
        prev.map(client => client.id === clientId ? updatedClient : client)
      );
    } catch (err: any) {
      // Revert optimistic update on error
      fetchClients();
      setError(err.message || 'Failed to move client up');
    } finally {
      setMovingClientId(null);
    }
  };

  const handleMoveClientDown = async (clientId: string) => {
    setMovingClientId(clientId);
    setError(null);
    
    // Optimistic update - immediately swap the clients in the UI
    const currentIndex = clients.findIndex(c => c.id === clientId);
    if (currentIndex < clients.length - 1) {
      const optimisticClients = [...clients];
      [optimisticClients[currentIndex], optimisticClients[currentIndex + 1]] = 
      [optimisticClients[currentIndex + 1], optimisticClients[currentIndex]];
      setClients(optimisticClients);
    }
    
    try {
      const updatedClient = await apiService.moveClientDown(clientId);
      
      // Update with the actual server response
      setClients(prev => 
        prev.map(client => client.id === clientId ? updatedClient : client)
      );
    } catch (err: any) {
      // Revert optimistic update on error
      fetchClients();
      setError(err.message || 'Failed to move client down');
    } finally {
      setMovingClientId(null);
    }
  };

  const getClientInitial = (name: string) => {
    return name.charAt(0).toUpperCase();
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} onRetry={fetchClients} />;

  return (
    <section>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 32 }}>
        <h2 style={{ fontWeight: 700, fontSize: 28, margin: 0, color: '#1f2937' }}>Clients</h2>
        {user?.canEdit ? (
          <button
            style={{
              background: '#3b82f6',
              color: '#fff',
              border: 'none',
              borderRadius: 12,
              padding: '12px 24px',
              fontWeight: 600,
              cursor: 'pointer',
              fontSize: 14,
              transition: 'all 0.2s ease',
              boxShadow: '0 2px 4px rgba(59, 130, 246, 0.2)'
            }}
            onClick={() => setShowAdd((v) => !v)}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#2563eb';
              e.currentTarget.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#3b82f6';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            {showAdd ? 'Cancel' : '+ Add Client'}
          </button>
        ) : null}
      </div>

      {showAdd && user?.canEdit ? (
        <div style={{
          marginBottom: 32,
          background: '#f8fafc',
          padding: 24,
          borderRadius: 16,
          border: '1px solid #e2e8f0',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ display: 'flex', gap: 16 }}>
              <input
                type="text"
                placeholder="Client name"
                value={newClient.name}
                onChange={e => setNewClient(c => ({ ...c, name: e.target.value }))}
                style={{
                  flex: 1,
                  padding: '12px 16px',
                  borderRadius: 10,
                  border: '1px solid #d1d5db',
                  fontSize: 15,
                  outline: 'none',
                  transition: 'border-color 0.2s ease'
                }}
                onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
              />
              <input
                type="text"
                placeholder="Country"
                value={newClient.country}
                onChange={e => setNewClient(c => ({ ...c, country: e.target.value }))}
                style={{
                  flex: 1,
                  padding: '12px 16px',
                  borderRadius: 10,
                  border: '1px solid #d1d5db',
                  fontSize: 15,
                  outline: 'none',
                  transition: 'border-color 0.2s ease'
                }}
                onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
              />
            </div>
            <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
              <input
                type="text"
                placeholder="Short description (optional)"
                value={newClient.shortDescription}
                onChange={e => setNewClient(c => ({ ...c, shortDescription: e.target.value }))}
                style={{
                  flex: 1,
                  padding: '12px 16px',
                  borderRadius: 10,
                  border: '1px solid #d1d5db',
                  fontSize: 15,
                  outline: 'none',
                  transition: 'border-color 0.2s ease'
                }}
                onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
              />
              <button
                onClick={handleAddClient}
                disabled={adding}
                style={{
                  background: '#10b981',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 10,
                  padding: '12px 20px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontSize: 14,
                  transition: 'background 0.2s ease'
                }}
                onMouseEnter={(e) => !adding && (e.currentTarget.style.background = '#059669')}
                onMouseLeave={(e) => !adding && (e.currentTarget.style.background = '#10b981')}
              >
                {adding ? 'Adding...' : 'Add'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))',
        gap: 24
      }}>
        {clients.length === 0 ? (
          <div style={{
            gridColumn: '1 / -1',
            padding: 48,
            textAlign: 'center',
            color: '#6b7280',
            background: '#fff',
            borderRadius: 16,
            border: '1px solid #e5e7eb'
          }}>
            <BusinessIcon style={{ fontSize: 48, color: '#d1d5db', marginBottom: 16 }} />
            <div style={{ fontSize: 18, fontWeight: 500, marginBottom: 8 }}>No clients found</div>
            <div style={{ fontSize: 14 }}>Add your first client to get started</div>
          </div>
        ) : (
          clients.map(client => (
            <div
              key={client.id}
              style={{
                background: '#fff',
                borderRadius: 16,
                border: '1px solid #e5e7eb',
                padding: 24,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
              }}
              onClick={() => navigate(`/clients/${client.id}`)}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.15)';
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.borderColor = '#3b82f6';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.borderColor = '#e5e7eb';
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
                <div style={{ marginRight: 16 }}>
                  {client.logo ? (
                    <img
                      src={client.logo}
                      alt={`${client.name} logo`}
                      style={{
                        width: 48,
                        height: 48,
                        borderRadius: 12,
                        objectFit: 'contain',
                        border: '2px solid #f3f4f6'
                      }}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        const fallback = target.nextElementSibling as HTMLElement;
                        if (fallback) fallback.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div
                    style={{
                      width: 48,
                      height: 48,
                      borderRadius: 12,
                      background: '#374151',
                      color: '#fff',
                      display: client.logo ? 'none' : 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 18,
                      fontWeight: 700,
                      textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)'
                    }}
                  >
                    {getClientInitial(client.name)}
                  </div>
                </div>
                <div style={{ flex: 1 }}>
                  <h3 style={{
                    margin: 0,
                    fontSize: 20,
                    fontWeight: 700,
                    color: '#1f2937',
                    marginBottom: 4
                  }}>
                    {client.name}
                  </h3>
                  <div style={{
                    fontSize: 14,
                    color: '#6b7280',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4
                  }}>
                    <BusinessIcon style={{ fontSize: 16 }} />
                    {client.country}
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {user?.canEdit ? (
                    <>
                      {/* Ordering buttons */}
                      <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMoveClientUp(client.id);
                          }}
                          disabled={movingClientId === client.id}
                          style={{ 
                            background: 'transparent',
                            color: '#6b7280', 
                            border: '1px solid #d1d5db', 
                            borderRadius: 6, 
                            padding: '6px', 
                            cursor: 'pointer',
                            fontSize: 12,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.2s ease',
                            minWidth: '32px',
                            height: '32px'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#f9fafb';
                            e.currentTarget.style.borderColor = '#9ca3af';
                            e.currentTarget.style.color = '#374151';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'transparent';
                            e.currentTarget.style.borderColor = '#d1d5db';
                            e.currentTarget.style.color = '#6b7280';
                          }}
                        >
                          <KeyboardArrowUpIcon style={{ fontSize: 16 }} />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMoveClientDown(client.id);
                          }}
                          disabled={movingClientId === client.id}
                          style={{ 
                            background: 'transparent',
                            color: '#6b7280', 
                            border: '1px solid #d1d5db', 
                            borderRadius: 6, 
                            padding: '6px', 
                            cursor: 'pointer',
                            fontSize: 12,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.2s ease',
                            minWidth: '32px',
                            height: '32px'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#f9fafb';
                            e.currentTarget.style.borderColor = '#9ca3af';
                            e.currentTarget.style.color = '#374151';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'transparent';
                            e.currentTarget.style.borderColor = '#d1d5db';
                            e.currentTarget.style.color = '#6b7280';
                          }}
                        >
                          <KeyboardArrowDownIcon style={{ fontSize: 16 }} />
                        </button>
                      </div>
                      {/* Edit and Delete buttons */}
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditClient(client);
                          }}
                          style={{ 
                            background: 'transparent',
                            color: '#3b82f6', 
                            border: '1px solid #bfdbfe', 
                        borderRadius: 8, 
                        padding: '8px 12px', 
                            cursor: 'pointer',
                        fontSize: 12,
                            display: 'flex',
                            alignItems: 'center',
                        gap: 4,
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#eff6ff';
                            e.currentTarget.style.borderColor = '#3b82f6';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'transparent';
                            e.currentTarget.style.borderColor = '#bfdbfe';
                          }}
                        >
                      <EditIcon style={{ fontSize: 14 }} />
                          Edit
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteClient(client.id);
                          }}
                          disabled={deletingId === client.id}
                          style={{ 
                            background: 'transparent',
                            color: '#ef4444', 
                            border: '1px solid #fecaca', 
                        borderRadius: 8, 
                        padding: '8px 12px', 
                            cursor: 'pointer',
                        fontSize: 12,
                            display: 'flex',
                            alignItems: 'center',
                        gap: 4,
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#fef2f2';
                            e.currentTarget.style.borderColor = '#f87171';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'transparent';
                            e.currentTarget.style.borderColor = '#fecaca';
                          }}
                        >
                      <DeleteIcon style={{ fontSize: 14 }} />
                      {deletingId === client.id ? 'Deleting...' : 'Delete'}
                        </button>
                      </div>
                    </>
                  ) : null}
                </div>
              </div>

              <div style={{
                background: '#f8fafc',
                borderRadius: 8,
                padding: 12,
                marginBottom: 16
              }}>
                <div style={{
                  fontSize: 13,
                  color: '#6b7280',
                  marginBottom: 4,
                  fontWeight: 500
                }}>
                  Description
                </div>
                <div style={{
                  fontSize: 14,
                  color: '#374151',
                  lineHeight: 1.4
                }}>
                  {client.shortDescription || 'No description available'}
                </div>
              </div>

              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                color: '#6b7280',
                fontSize: 13
              }}>
                <UpdateIcon style={{ fontSize: 16 }} />
                <span>Last updated: {client.lastUpdated || 'Unknown'}</span>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Edit Dialog */}
      {editingClient && (
        <ClientEditDialog
          isOpen={showEditDialog}
          onClose={handleCloseEditDialog}
          client={editingClient}
          onSave={handleClientUpdated}
        />
      )}
    </section>
  );
};

export default ClientList; 